import React, { useState, useMemo, useEffect } from 'react';
import {
  FaGraduationCap, FaCheckCircle, FaSpinner, FaClock,
  FaBookOpen, FaStar, FaSearch, FaChevronDown, FaChevronUp,
  FaBook, FaCertificate, FaTable, FaCheck,
  FaUniversity, FaRocket, FaAward,
  FaBolt, FaBullseye, FaExclamationTriangle
} from 'react-icons/fa';
import { postData } from '../../../../services/apiService';
import { useSelector } from 'react-redux';

// ============================
//  CONFIGURATIONS
// ============================
const statusConfig = {
  completed: { color: 'bg-emerald-500 text-white', icon: FaCheckCircle, label: 'Completed' },
  'in-progress': { color: 'bg-amber-500 text-white', icon: FaSpinner, label: 'In Progress' },
  planned: { color: 'bg-slate-400 text-white', icon: FaClock, label: 'Planned' },
  InProcess: { color: 'bg-amber-500 text-white', icon: FaSpinner, label: 'In Progress' },
  Completed: { color: 'bg-emerald-500 text-white', icon: FaCheckCircle, label: 'Completed' },
  '': { color: 'bg-slate-400 text-white', icon: FaClock, label: 'Planned' },
};

const typeConfig = {
  Core: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Core' },
  Elective: { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Elective' },
};

// ============================
//  MAIN COMPONENT
// ============================
const CurriculumManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [semesterData, setSemesterData] = useState(null);
  const [expandedSemesters, setExpandedSemesters] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const user = useSelector((state) => state.user.user);

  const [apiParams] = useState({
    roll_No: user?.roll_no,
    batch: user?.batch,
    inst_ID: user?.inst_id,
    sem_No: user?.semNo,
    cour_Id: user?.course_id
  });

  useEffect(() => {
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await postData('StudentDetails/GetStudentSubjects', apiParams);
      
      if (response && response.semesters) {
        setSemesterData(response);
        const currentSem = response.semesters.find(s => s.sem_Status === 'InProcess');
        if (currentSem) {
          setExpandedSemesters(new Set([currentSem.sem_No]));
        } else if (response.semesters.length > 0) {
          setExpandedSemesters(new Set([response.semesters[0].sem_No]));
        }
      } else {
        setError('No data received from API');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch student data');
      console.error('Error fetching student data:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSemester = (semesterId) => {
    setExpandedSemesters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(semesterId)) newSet.delete(semesterId);
      else newSet.add(semesterId);
      return newSet;
    });
  };

  const setActiveSemester = (semesterId) => {
    setExpandedSemesters(new Set([semesterId]));
  };

  const transformedData = useMemo(() => {
    if (!semesterData) return null;

    const totalCredits = semesterData.semesters?.reduce((sum, sem) => 
      sum + sem.subjects.reduce((s, sub) => s + (sub.credits || 0), 0), 0
    ) || 0;

    const completedCredits = semesterData.semesters?.reduce((sum, sem) => {
      if (sem.sem_Status === 'Completed') {
        return sum + sem.subjects.reduce((s, sub) => s + (sub.credits || 0), 0);
      }
      return sum;
    }, 0) || 0;

    return {
      student: {
        name: semesterData.name || "Student",
        id: semesterData.roll_No || "N/A",
        program: semesterData.cour_LName || "Program",
        totalCreditsRequired: totalCredits,
        completedCredits: completedCredits,
        currentSemester: semesterData.semesters?.find(s => s.sem_Status === 'InProcess')?.sem_No || 
                        semesterData.semesters?.[0]?.sem_No || 1,
      },
      semesters: semesterData.semesters?.map(sem => {
        let status = 'planned';
        if (sem.sem_Status === 'InProcess') status = 'in-progress';
        else if (sem.sem_Status === 'Completed') status = 'completed';
        else if (sem.sem_Status === '') status = 'planned';
        
        return {
          id: sem.sem_No,
          name: `Semester ${sem.sem_No}`,
          status: status,
          sem_Status: sem.sem_Status || 'planned',
          isCurrent: sem.sem_Status === 'InProcess',
          subjects: sem.subjects || [],
          corePapers: sem.subjects?.filter(s => s.subjectType === 'Core') || [],
          electivePapers: sem.subjects?.filter(s => s.subjectType === 'Elective') || [],
        };
      }) || []
    };
  }, [semesterData]);

  const filteredSemesters = useMemo(() => {
    if (!transformedData) return [];

    return transformedData.semesters.map(semester => ({
      ...semester,
      subjects: semester.subjects.filter(paper => {
        const matchStatus = filter === 'all' || semester.status === filter;
        const matchSearch = (paper.subj_Name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (paper.subj_Code || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchStatus && matchSearch;
      })
    })).filter(semester => semester.subjects.length > 0);
  }, [filter, searchTerm, transformedData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d4f5c]/5 via-white to-[#1a7a8a]/5 p-5 md:p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0d4f5c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading curriculum data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d4f5c]/5 via-white to-[#1a7a8a]/5 p-5 md:p-8 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaExclamationTriangle className="text-3xl text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">Error Loading Data</h3>
          <p className="text-slate-600 mb-4">{error}</p>
          <button 
            onClick={fetchStudentData}
            className="px-6 py-2 bg-[#0d4f5c] text-white rounded-xl hover:bg-[#1a7a8a] transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!transformedData || !transformedData.semesters || transformedData.semesters.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d4f5c]/5 via-white to-[#1a7a8a]/5 p-5 md:p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaTable className="text-3xl text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No Curriculum Data</h3>
          <p className="text-slate-600">No semesters or subjects found for this student.</p>
        </div>
      </div>
    );
  }

  const { student } = transformedData;
  const currentSemesterId = student.currentSemester || 1;

  const allPapers = transformedData.semesters.flatMap(s => s.subjects);
  const totalPapers = allPapers.length;
  const completedPapers = allPapers.filter(p => {
    const sem = transformedData.semesters.find(s => s.subjects.includes(p));
    return sem?.status === 'completed';
  }).length;
  const inProgressPapers = allPapers.filter(p => {
    const sem = transformedData.semesters.find(s => s.subjects.includes(p));
    return sem?.status === 'in-progress';
  }).length;
  const plannedPapers = allPapers.filter(p => {
    const sem = transformedData.semesters.find(s => s.subjects.includes(p));
    return sem?.status === 'planned';
  }).length;

  const activeSemesterName = expandedSemesters.size > 0
    ? transformedData.semesters.find(s => expandedSemesters.has(s.id))?.name
    : 'None';

  const stats = [
    { icon: FaBookOpen, label: 'Total Papers', value: totalPapers, color: 'from-[#0d4f5c] to-[#1a7a8a]' },
    { icon: FaCheckCircle, label: 'Completed', value: completedPapers, color: 'from-emerald-500 to-emerald-600' },
    { icon: FaSpinner, label: 'In Progress', value: inProgressPapers, color: 'from-amber-500 to-amber-600' },
    { icon: FaClock, label: 'Planned', value: plannedPapers, color: 'from-slate-500 to-slate-600' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d4f5c]/5 via-white to-[#1a7a8a]/5 p-5 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0d4f5c] to-[#1a7a8a] flex items-center justify-center text-white shadow-xl shadow-[#0d4f5c]/20">
              <FaGraduationCap className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Curriculum Management</h1>
              <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                <span className="font-semibold text-[#0d4f5c]">{student.name}</span>
                <span className="text-slate-300">•</span>
                <span>{student.id}</span>
                <span className="text-slate-300">•</span>
                <span>{student.program}</span>
                {activeSemesterName !== 'None' && (
                  <span className="inline-flex items-center gap-1.5 bg-[#1a7a8a] text-white px-3 py-0.5 rounded-full text-xs font-semibold shadow-md animate-pulse">
                    <FaBullseye className="text-[10px]" />
                    Viewing: {activeSemesterName}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-2xl shadow-lg border border-slate-200/60">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-400 flex items-center justify-center text-white shadow-lg shadow-amber-500/30">
                <FaAward className="text-lg" />
              </div>
              <div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">GPA</div>
                <div className="text-xl font-bold text-slate-800">N/A</div>
              </div>
            </div>
            <div className="w-px h-10 bg-slate-200" />
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#0d4f5c] flex items-center justify-center text-white shadow-lg shadow-[#0d4f5c]/30">
                <FaBookOpen className="text-lg" />
              </div>
              <div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Credits</div>
                <div className="text-xl font-bold text-slate-800">{student.completedCredits}/{student.totalCreditsRequired}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-slate-200/60 hover:shadow-xl transition-all hover:-translate-y-0.5">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white shadow-lg`}>
                  <stat.icon className="text-lg" />
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{stat.label}</div>
                  <div className="text-2xl font-bold text-slate-800">
                    {stat.value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white/80 backdrop-blur-sm p-4 rounded-2xl shadow-lg border border-slate-200/60 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-[#0d4f5c] uppercase tracking-wider flex items-center gap-1">
              <FaBolt className="text-[10px]" />
              Filter
            </span>
            {['all', 'completed', 'in-progress', 'planned'].map((status, i) => (
              <button
                key={i}
                onClick={() => setFilter(status)}
                className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all capitalize ${filter === status
                    ? 'bg-[#0d4f5c] text-white shadow-md scale-105'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                {status === 'all' ? 'All' : status}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => setExpandedSemesters(new Set(transformedData.semesters.map(s => s.id)))}
              className="text-xs text-[#0d4f5c] font-medium px-3.5 py-1.5 rounded-full border border-[#0d4f5c]/30 hover:bg-[#0d4f5c]/5 transition">
              Expand All
            </button>
            <button onClick={() => setExpandedSemesters(new Set([currentSemesterId]))}
              className="text-xs text-[#1a7a8a] font-medium px-3.5 py-1.5 rounded-full border border-[#1a7a8a]/30 hover:bg-[#1a7a8a]/5 transition">
              <FaBullseye className="inline mr-1.5 text-[10px]" />
              Current
            </button>
            <button onClick={() => setExpandedSemesters(new Set())}
              className="text-xs text-slate-500 font-medium px-3.5 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 transition">
              Collapse All
            </button>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0d4f5c]/40 text-sm" />
              <input
                type="text"
                placeholder="Search by code or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-1.5 border border-slate-200 rounded-xl text-sm w-full sm:w-52 focus:outline-none focus:ring-2 focus:ring-[#0d4f5c]/30 focus:border-[#0d4f5c] bg-white/80 transition"
              />
            </div>
          </div>
        </div>

        {/* Semesters */}
        <div className="space-y-4">
          {filteredSemesters.length === 0 ? (
            <div className="text-center py-16 bg-white/60 rounded-3xl border-2 border-dashed border-slate-300">
              <FaTable className="text-4xl text-[#0d4f5c]/20 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No papers match your filter or search.</p>
            </div>
          ) : (
            filteredSemesters.map((semester, idx) => (
              <SemesterSection
                key={idx}
                semester={semester}
                isExpanded={expandedSemesters.has(semester.id)}
                onToggle={toggleSemester}
                setActiveSemester={setActiveSemester}
                isCurrent={semester.id === currentSemesterId}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ============================
//  SEMESTER SECTION COMPONENT
// ============================
const SemesterSection = ({ semester, isExpanded, onToggle, setActiveSemester, isCurrent }) => {
  const semesterStatus = semester.sem_Status || semester.status || 'planned';
  const StatusIcon = statusConfig[semesterStatus]?.icon || FaClock;

  const allPapers = semester.subjects || [];
  
  const totalCredits = allPapers.reduce((sum, p) => sum + (p.credits || 0), 0);

  const handleToggle = () => {
    if (isExpanded) onToggle(semester.id);
    else setActiveSemester(semester.id);
  };

  const statusColors = {
    completed: 'border-emerald-300/60 bg-emerald-50/40',
    'in-progress': 'border-amber-300/60 bg-amber-50/40',
    planned: 'border-slate-300/60 bg-slate-50/40',
    InProcess: 'border-amber-300/60 bg-amber-50/40',
    Completed: 'border-emerald-300/60 bg-emerald-50/40',
    '': 'border-slate-300/60 bg-slate-50/40',
  };

  const statusBadgeColors = {
    completed: 'bg-emerald-500 text-white',
    'in-progress': 'bg-amber-500 text-white',
    planned: 'bg-slate-500 text-white',
    InProcess: 'bg-amber-500 text-white',
    Completed: 'bg-emerald-500 text-white',
    '': 'bg-slate-500 text-white',
  };

  const corePapers = allPapers.filter(p => p.subjectType === 'Core');
  const electivePapers = allPapers.filter(p => p.subjectType === 'Elective');

  return (
    <div className={`border-2 rounded-2xl overflow-hidden transition-all duration-300 ${statusColors[semesterStatus] || statusColors.planned} ${isExpanded ? 'shadow-lg scale-[1.005]' : 'shadow-sm'} ${isCurrent ? 'ring-2 ring-[#1a7a8a] ring-offset-2' : ''}`}>
      <button
        onClick={handleToggle}
        className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-white/30 transition-colors group"
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl ${statusBadgeColors[semesterStatus] || statusBadgeColors.planned} flex items-center justify-center text-white shadow transition-transform group-hover:scale-105 relative`}>
            <FaUniversity className="text-lg" />
            {isCurrent && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#1a7a8a] rounded-full flex items-center justify-center shadow animate-pulse">
                <FaBullseye className="text-[10px] text-white" />
              </div>
            )}
          </div>
          <div className="text-left">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-lg font-bold text-slate-800">{semester.name}</span>
              {isCurrent && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-[#1a7a8a] to-[#0d4f5c] text-white shadow">
                  <FaBullseye className="text-[10px]" />
                  Current
                </span>
              )}
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full ${statusBadgeColors[semesterStatus] || statusBadgeColors.planned} shadow-sm`}>
                <StatusIcon className="text-xs" />
                {semesterStatus === 'InProcess' ? 'In Progress' : 
                 semesterStatus === 'Completed' ? '✓ Complete' : 
                 semesterStatus === 'completed' ? '✓ Complete' : 
                 semesterStatus === 'in-progress' ? 'In Progress' : 
                 semesterStatus === '' ? 'Planned' : 'Planned'}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-sm text-slate-500">
              <span><span className="font-semibold">{corePapers.length}</span> Core</span>
              {electivePapers.length > 0 && (
                <>
                  <span className="text-slate-300">+</span>
                  <span><span className="font-semibold">{electivePapers.length}</span> Elective</span>
                </>
              )}
              <span className="text-slate-300">|</span>
              <span>Credits: <span className="font-bold text-[#0d4f5c]">{totalCredits}</span></span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-[#0d4f5c]/10 flex items-center justify-center group-hover:bg-[#0d4f5c]/20 transition-colors">
            {isExpanded ? <FaChevronUp className="text-[#0d4f5c]" /> : <FaChevronDown className="text-[#0d4f5c]" />}
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="px-5 pb-4 pt-2 bg-white/50 backdrop-blur-sm">
          {/* Core Papers */}
          {corePapers.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-0.5 h-5 bg-gradient-to-b from-[#0d4f5c] to-[#1a7a8a] rounded-full" />
                <h4 className="text-sm font-bold text-[#0d4f5c] flex items-center gap-2">
                  <FaBook className="text-[#1a7a8a] text-sm" />
                  Core Papers
                  <span className="text-xs font-normal text-slate-500 ml-1">({corePapers.length} papers)</span>
                </h4>
                <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-200/60 shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] text-white text-xs">
                    <tr>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">S.No</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Code</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Subject</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Type</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Credits</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {corePapers.map((paper, idx) => {
                      const status = semesterStatus === 'InProcess' ? 'in-progress' : 
                                    semesterStatus === 'Completed' ? 'completed' : 
                                    semesterStatus || 'planned';
                      const statusInfo = statusConfig[status] || statusConfig.planned;
                      const StatusIcon = statusInfo.icon;
                      const typeInfo = typeConfig[paper.subjectType] || { color: 'bg-blue-100 text-blue-700 border-blue-200', label: paper.subjectType || 'Core' };
                      
                      return (
                        <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-3 py-2 text-slate-500 font-medium text-center text-sm">{idx + 1}</td>
                          <td className="px-3 py-2">
                            <span className="text-xs font-mono font-semibold px-2.5 py-1 rounded bg-[#0d4f5c]/10 text-[#0d4f5c] border border-[#0d4f5c]/10 whitespace-nowrap">
                              {paper.subj_Code}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-sm font-semibold text-slate-700">{paper.subj_Name}</span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${typeInfo.color} whitespace-nowrap`}>
                              {typeInfo.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm font-bold text-[#0d4f5c]">{paper.credits}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${statusInfo.color} shadow-sm`}>
                              <StatusIcon className="text-xs" />
                              {statusInfo.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Elective Papers */}
          {electivePapers.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-0.5 h-5 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full" />
                <h4 className="text-sm font-bold text-purple-700 flex items-center gap-2">
                  <FaCertificate className="text-purple-600 text-sm" />
                  Elective Papers
                  <span className="text-xs font-normal text-slate-500 ml-1">({electivePapers.length} paper(s))</span>
                </h4>
                <div className="flex-1 h-px bg-gradient-to-r from-purple-200 to-transparent" />
              </div>
              <div className="overflow-x-auto rounded-xl border border-purple-200/60 shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-purple-600 to-purple-700 text-white text-xs">
                    <tr>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">S.No</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Code</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Subject</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Type</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Credits</th>
                      <th className="px-3 py-2.5 text-left font-semibold uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {electivePapers.map((paper, idx) => {
                      const status = semesterStatus === 'InProcess' ? 'in-progress' : 
                                    semesterStatus === 'Completed' ? 'completed' : 
                                    semesterStatus || 'planned';
                      const statusInfo = statusConfig[status] || statusConfig.planned;
                      const StatusIcon = statusInfo.icon;
                      const typeInfo = typeConfig[paper.subjectType] || { color: 'bg-purple-100 text-purple-700 border-purple-200', label: paper.subjectType || 'Elective' };
                      
                      return (
                        <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-3 py-2 text-slate-500 font-medium text-center text-sm">{idx + 1}</td>
                          <td className="px-3 py-2">
                            <span className="text-xs font-mono font-semibold px-2.5 py-1 rounded bg-purple-600/10 text-purple-700 border border-purple-200 whitespace-nowrap">
                              {paper.subj_Code}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-sm font-semibold text-slate-700">{paper.subj_Name}</span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${typeInfo.color} whitespace-nowrap`}>
                              {typeInfo.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm font-bold text-purple-700">{paper.credits}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${statusInfo.color} shadow-sm`}>
                              <StatusIcon className="text-xs" />
                              {statusInfo.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CurriculumManagement;